"use client";

import Script from "next/script";
import type { ComponentType } from "react";
import type { TelemetryUser } from "../telemetry.types";

type CommonRoomSignalCall = [string, unknown[]];

interface CommonRoomSignals extends Array<CommonRoomSignalCall> {
  _opts?: {
    apiHost: string;
  };
  identify: (data: { email: string; name?: string }) => CommonRoomSignals;
  page: (...args: unknown[]) => CommonRoomSignals;
  form: (data: Record<string, string>) => CommonRoomSignals;
}

declare global {
  interface Window {
    signals?: CommonRoomSignals;
  }
}

const DEFAULT_COMMON_ROOM_API_HOST = "https://api.cr-relay.com";
const CommonRoomScript = Script as unknown as ComponentType<{
  id: string;
  src: string;
  strategy: "afterInteractive";
}>;

function getCommonRoomSiteId(): string | undefined {
  return process.env.NEXT_PUBLIC_COMMON_ROOM_SITE_ID || undefined;
}

function getCommonRoomApiHost(): string {
  return process.env.NEXT_PUBLIC_COMMON_ROOM_API_HOST || DEFAULT_COMMON_ROOM_API_HOST;
}

function getCommonRoomScriptUrl(siteId: string): string {
  return `https://cdn.cr-relay.com/v1/site/${siteId}/signals.js`;
}

function queueSignalMethod(method: "identify" | "page" | "form") {
  return function queueSignalCall(this: CommonRoomSignals, ...args: unknown[]) {
    this.push([method, args]);
    return this;
  };
}

function ensureCommonRoomSignalsQueue(): CommonRoomSignals | undefined {
  if (typeof window === "undefined") return undefined;
  if (window.signals) return window.signals;

  const signals = Object.assign([], {
    _opts: { apiHost: getCommonRoomApiHost() },
    identify: queueSignalMethod("identify"),
    page: queueSignalMethod("page"),
    form: queueSignalMethod("form"),
  }) as CommonRoomSignals;

  window.signals = signals;
  return signals;
}

export function CommonRoomScripts() {
  const siteId = getCommonRoomSiteId();
  if (!siteId) return null;

  ensureCommonRoomSignalsQueue();

  return (
    <CommonRoomScript
      id="common-room-signals"
      src={getCommonRoomScriptUrl(siteId)}
      strategy="afterInteractive"
    />
  );
}

export function identifyCommonRoomUser(user: TelemetryUser) {
  if (!user.email) return;

  const signals = ensureCommonRoomSignalsQueue();
  signals?.identify({
    email: user.email,
    name: user.fullName ?? undefined,
  });
}
